import React from 'react';
import { styled } from 'styled-components';
import Text from '@ingka/text';
import { useTranslation } from 'react-i18next';
import Weekdays from './Weekdays/Weekdays';
import TimeSlots from './TimeSlots/TimeSlots';
import NextPrev from './NextPrev/NextPrev';
import WeekComponent from './Week/Week';
import useCalendar from '../../hooks/useCalendar';
import AdjacentWeeksRange from './AdjacentWeeksRange/AdjacentWeeksRange';
import colors from '../../styles/colors';
import { hasPendingSlot } from '../../helpers/slotHelper';
import Loading, { LoadingBall } from '@ingka/loading';
import { type Slot } from '../../services/appointment/model';
import { type HandlePendingSlotSelected } from '../../hooks/useGetAvailableTimeslots';
import { useStateSelector } from '../../store/';
import { selectSelectedOriginalService } from '../../reducers/selectors';
import NoTimeSlotsMessage from '../NoTimeSlotsMessage';
import { mediaQueries } from '../../styles/mediaQueries';
import {
  chunkArray,
  getAdjacentWeeksStartAndEndDate,
  type Weeks,
} from '../../helpers/timeSlotHelper';

interface StyledContainerProps {
  disabled: boolean | null | undefined;
}

export interface TimeSlotPickerProps {
  slots: Slot[];
  replacementServiceSlots: Slot[];
  onPendingSlotSelected: HandlePendingSlotSelected;
  onNextClick: (date: string) => void;
  onPrevClick: (date: string) => void;
  shouldStartOn: string;
  prevEnabled?: boolean;
  nextEnabled?: boolean;
  disabled?: boolean | null;
}

const StyledContainer = styled.div<StyledContainerProps>`
  ${(props): string => `
  display: flex;
  flex-direction: column;
  color: ${props.disabled ? colors.text.default : 'inherit'};
  pointer-events: ${props.disabled ? 'none' : 'auto'};
  margin-top: 1.5rem;

    ${mediaQueries.portraitTabletUp} {
      margin-top: 3.5rem;
      max-width: 558px;
    }
`}
`;

const StyledCalendar = styled.section`
  flex-grow: 1;
  flex-shrink: 0;
`;

const StyledWeeks = styled.div`
  position: relative;
  margin-bottom: 40px;
`;

const StyledMessageBox = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  font-size: 0.875rem;
  padding: 1rem;
  box-sizing: border-box;
  color: ${colors.text.black};
  background: ${colors.background.default};
  border: 1px solid #ccc;
  border-radius: 2px;
  width: calc(100vw - 2rem);
  max-width: 440px;
`;

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.2);
  z-index: 1;
`;

const StyledLoading = styled(Loading)`
  &&& {
    margin-bottom: 40px;
  }
`;

const TimeSlotPicker: React.FC<TimeSlotPickerProps> = ({
  slots,
  replacementServiceSlots,
  onPendingSlotSelected,
  onNextClick,
  onPrevClick,
  shouldStartOn = '',
  prevEnabled = true,
  nextEnabled = true,
  disabled,
}) => {
  const { t } = useTranslation();
  const [
    {
      selectedDate,
      selectedSlot,
      selectedReplacementServiceSlot,
      calendarDays,
      showReplacementServiceSlots,
      numberOfWeeks,
      replacementServiceSlotsEnabled,
    },
    {
      handleSelectTimeSlot,
      handleSelectSlot,
      handleSwitchChange,
      handleEnableReplacementServiceSlots,
    },
  ] = useCalendar(
    shouldStartOn,
    slots,
    replacementServiceSlots,
    onPendingSlotSelected,
  );
  const originalService = useStateSelector(selectSelectedOriginalService);
  const { replacementService } = originalService || {};

  const { date: firstDate } = calendarDays[0];
  const { date: lastDate } = calendarDays[calendarDays.length - 1];

  const weeks = chunkArray(calendarDays, 7);
  const upperAdjacentWeeks: Weeks = weeks.slice(0, 4);
  const upperAdjacentWeeksRange =
    getAdjacentWeeksStartAndEndDate(upperAdjacentWeeks);
  const lowerAdjacentWeeks: Weeks = weeks.slice(4, 8);
  const lowerAdjacentWeeksRange =
    getAdjacentWeeksStartAndEndDate(lowerAdjacentWeeks);

  const selectableDatesCount = calendarDays.filter(
    (day) =>
      day.slot || (showReplacementServiceSlots && day.replacementServiceSlot),
  ).length;

  const radioGroupLabel = selectableDatesCount
    ? `${t('pages.timeslots.timePickerTitle')}, ${t('pages.timeslots.ariaAvailableDatesCount', { count: selectableDatesCount })}`
    : t('pages.timeslots.timePickerTitle');

  return (
    <StyledContainer disabled={disabled} data-cy="time-slot-picker">
      <StyledCalendar>
        <Text tagName="h3" textStyle="Heading.S" id="timeslots-date-heading">
          {t('pages.timeslots.timePickerTitle')}
        </Text>
        <NextPrev
          firstDate={firstDate}
          lastDate={lastDate}
          numberOfWeeks={numberOfWeeks}
          onNextClick={onNextClick}
          onPrevClick={onPrevClick}
          prevEnabled={prevEnabled}
          nextEnabled={nextEnabled}
          loading={hasPendingSlot(selectedSlot, selectedReplacementServiceSlot)}
        >
          <AdjacentWeeksRange start={firstDate} end={lastDate} />
        </NextPrev>
        <Weekdays calendarDays={calendarDays} />
        <StyledWeeks role="radiogroup" aria-label={radioGroupLabel}>
          {numberOfWeeks > 4 && upperAdjacentWeeksRange && (
            <div aria-hidden="true">
              <AdjacentWeeksRange
                start={upperAdjacentWeeksRange.firstDayDate}
                end={upperAdjacentWeeksRange.lastDayDate}
              />
            </div>
          )}
          {hasPendingSlot(selectedSlot, selectedReplacementServiceSlot) && (
            <Overlay
              aria-hidden="true"
              onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                event.stopPropagation();
                event.preventDefault();
              }}
            />
          )}
          {upperAdjacentWeeks.map((week) => (
            <WeekComponent
              key={week[0].date}
              days={week}
              selectedDate={selectedDate}
              onSelectSlot={handleSelectSlot}
              showReplacementServiceSlots={showReplacementServiceSlots}
              loading={hasPendingSlot(
                selectedSlot,
                selectedReplacementServiceSlot,
              )}
            />
          ))}
          {lowerAdjacentWeeksRange && (
            <div aria-hidden="true">
              <AdjacentWeeksRange
                start={lowerAdjacentWeeksRange.firstDayDate}
                end={lowerAdjacentWeeksRange.lastDayDate}
              />
            </div>
          )}
          {hasPendingSlot(selectedSlot, selectedReplacementServiceSlot) && (
            <Overlay
              aria-hidden="true"
              onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                event.stopPropagation();
                event.preventDefault();
              }}
            />
          )}
          {lowerAdjacentWeeks.map((week) => (
            <WeekComponent
              key={week[0].date}
              days={week}
              selectedDate={selectedDate}
              onSelectSlot={handleSelectSlot}
              showReplacementServiceSlots={showReplacementServiceSlots}
              loading={hasPendingSlot(
                selectedSlot,
                selectedReplacementServiceSlot,
              )}
            />
          ))}
          {slots.length === 0 && !replacementServiceSlotsEnabled && (
            <StyledMessageBox tabIndex={0} role="status">
              <NoTimeSlotsMessage
                showReplacementServiceButton={
                  replacementServiceSlots.length > 0
                }
                serviceId={originalService?.serviceId || ''}
                replacementService={replacementService}
                onButtonClick={handleEnableReplacementServiceSlots}
              />
            </StyledMessageBox>
          )}
        </StyledWeeks>
        {hasPendingSlot(selectedSlot, selectedReplacementServiceSlot) && (
          <StyledLoading text={t('components.loading.title.timeslots')}>
            <LoadingBall />
          </StyledLoading>
        )}
      </StyledCalendar>
      {!hasPendingSlot(selectedSlot, selectedReplacementServiceSlot) &&
        selectedDate && (
          <TimeSlots
            slot={selectedSlot}
            replacementServiceSlot={selectedReplacementServiceSlot}
            onSelectTimeSlot={handleSelectTimeSlot}
            onSwitchChange={handleSwitchChange}
            replacementServiceSlotsEnabled={replacementServiceSlotsEnabled}
            showReplacementServiceSlots={showReplacementServiceSlots}
          />
        )}
    </StyledContainer>
  );
};

export default TimeSlotPicker;
